import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Check, X, ClipboardCheck, AlertTriangle } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth, isRespAdmin } from "@/context/AuthContext";
import { Empty, toISO, BulkCheck, BulkBar, ConfirmAction, BadgeFoto } from "@/components/Shared";
import { useRefreshOnPull } from "@/components/PullToRefresh";

function RigaPersona({ persona, stato, onSet, testPrefix, bulkMode, isSelected, onToggleSel }) {
  return (
    <div className={`flex items-center gap-3 py-2.5 border-b border-slate-50 last:border-0 rounded-xl px-2 -mx-2 ${bulkMode && isSelected ? "bg-amber-50" : ""}`}
      data-testid={`${testPrefix}-riga-${persona.id}`}>
      {bulkMode && <BulkCheck checked={isSelected} onClick={onToggleSel} testId={`${testPrefix}-check-${persona.id}`} />}
      <p className="font-bold text-slate-800 text-sm flex-1 truncate">{persona.nome} {persona.cognome}</p>
      {/* Il divieto di ripresa va visto da chi è in campo con i bambini in mano,
          non solo dalla Segreteria: e' qui che si decide se scattare. */}
      <BadgeFoto animato={persona} className="!text-[10px] shrink-0" />
      {!bulkMode && (
        <>
          <button onClick={() => onSet(true)}
            className={`h-9 w-9 rounded-full flex items-center justify-center transition-all active:scale-90 ${stato === true ? "bg-emerald-500 text-white shadow-md" : "bg-slate-100 text-slate-400 hover:bg-emerald-100"}`}
            data-testid={`${testPrefix}-presente-${persona.id}`}>
            <Check className="h-4 w-4" />
          </button>
          <button onClick={() => onSet(false)}
            className={`h-9 w-9 rounded-full flex items-center justify-center transition-all active:scale-90 ${stato === false ? "bg-red-500 text-white shadow-md" : "bg-slate-100 text-slate-400 hover:bg-red-100"}`}
            data-testid={`${testPrefix}-assente-${persona.id}`}>
            <X className="h-4 w-4" />
          </button>
        </>
      )}
      {bulkMode && stato != null && (
        <span className={`text-xs font-bold shrink-0 ${stato ? "text-emerald-500" : "text-red-500"}`}>{stato ? "Pres." : "Ass."}</span>
      )}
    </div>
  );
}

export default function Presenze() {
  const { user } = useAuth();
  const canStaff = isRespAdmin(user);
  const oggi = toISO(new Date());
  const [data, setData] = useState(oggi);
  const [squadre, setSquadre] = useState([]);
  const [squadraId, setSquadraId] = useState("");
  const [ctx, setCtx] = useState(null);

  const [bulkMode, setBulkMode] = useState(false);
  const [selected, setSelected] = useState([]); // ["animato:id", "staff:id", ...]

  useEffect(() => {
    if (canStaff) {
      api.get("/squadre").then((r) => {
        setSquadre(r.data);
        if (r.data.length && !squadraId) setSquadraId(r.data[0].id);
      });
    } else {
      api.get("/squadre/mia").then((r) => {
        if (r.data) { setSquadre([r.data]); setSquadraId(r.data.id); }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canStaff]);

  const load = useCallback(() => {
    if (!squadraId) return;
    api.get(`/presenze/contesto?data=${data}&squadra_id=${squadraId}`).then((r) => setCtx(r.data)).catch((err) => {
      setCtx(null);
      toast.error(errMsg(err));
    });
  }, [data, squadraId]);
  useEffect(() => { load(); }, [load]);
  useRefreshOnPull(load);

  // Reset selection quando cambia contesto (data / squadra)
  useEffect(() => { setSelected([]); }, [data, squadraId]);

  const segna = async (personaId, tipo, presente) => {
    try {
      await api.post("/presenze", { data, squadra_id: squadraId, persona_id: personaId, tipo, presente });
      setCtx((c) => ({ ...c, presenze: { ...c.presenze, [`${tipo}:${personaId}`]: presente } }));
    } catch (err) { toast.error(errMsg(err)); }
  };

  const toggleSel = (key) => setSelected((s) => s.includes(key) ? s.filter((x) => x !== key) : [...s, key]);
  const exitBulk = () => { setBulkMode(false); setSelected([]); };

  const totalePersone = useMemo(() => {
    if (!ctx) return 0;
    return ctx.animati.length + (canStaff ? ctx.staff.length : 0);
  }, [ctx, canStaff]);

  const selectAll = () => {
    if (!ctx) return;
    const all = [
      ...ctx.animati.map((a) => `animato:${a.id}`),
      ...(canStaff ? ctx.staff.map((s) => `staff:${s.id}`) : []),
    ];
    setSelected(selected.length === all.length ? [] : all);
  };

  const bulkSegna = async (presente) => {
    if (!selected.length) return;
    const updates = selected.map((k) => {
      const [tipo, persona_id] = k.split(":");
      return { tipo, persona_id };
    });
    try {
      const { data: r } = await api.post("/presenze/bulk", { data, squadra_id: squadraId, updates, presente });
      toast.success(`${r.updated} persone segnate ${presente ? "presenti" : "assenti"}`);
      // aggiorna stato locale
      setCtx((c) => {
        const nuove = { ...c.presenze };
        updates.forEach((u) => { nuove[`${u.tipo}:${u.persona_id}`] = presente; });
        return { ...c, presenze: nuove };
      });
      exitBulk();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div data-testid="presenze-page">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900">Presenze</h1>
          <p className="text-sm text-slate-500 font-semibold mb-6">{canStaff ? "Registrazione per squadra nei giorni attivi" : "Segna le presenze della tua squadra oggi"}</p>
        </div>
        {ctx && totalePersone > 0 && ctx.giorno_attivo && (
          <button className={bulkMode ? "btn-secondary" : "btn-ghost"} onClick={() => bulkMode ? exitBulk() : setBulkMode(true)} data-testid="presenze-bulk-toggle">
            {bulkMode ? "Annulla selezione" : "Seleziona"}
          </button>
        )}
      </div>

      <div className="flex gap-3 mb-5 flex-wrap">
        {canStaff ? (
          <input type="date" className="input-app max-w-[180px]" value={data} onChange={(e) => setData(e.target.value)} data-testid="presenze-data-input" />
        ) : (
          <div className="rounded-2xl bg-amber-50 border border-amber-100 px-4 py-2 text-sm font-extrabold text-amber-700" data-testid="presenze-data-oggi">
            Oggi · {new Date(`${oggi}T12:00:00`).toLocaleDateString("it-IT", { day: "numeric", month: "long" })}
          </div>
        )}
        {canStaff ? (
          <select className="input-app max-w-[220px]" value={squadraId} onChange={(e) => setSquadraId(e.target.value)} data-testid="presenze-squadra-select">
            {squadre.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
          </select>
        ) : (
          squadre[0] && <span className="badge-info self-center !text-sm !py-2 !px-4">{squadre[0].nome}</span>
        )}
      </div>

      {bulkMode && (
        <BulkBar count={selected.length} total={totalePersone} onSelectAll={selectAll} onClear={exitBulk} testId="presenze-bulk-bar"
          actions={
            <>
              <ConfirmAction onConfirm={() => bulkSegna(true)} title={`Segnare ${selected.length} persone come PRESENTI?`} testId="presenze-bulk-presente-confirm">
                <button type="button" disabled={selected.length === 0} className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold px-3 py-2 disabled:opacity-40 inline-flex items-center gap-1.5" data-testid="presenze-bulk-presente-btn">
                  <Check className="h-3.5 w-3.5" /> Segna presenti
                </button>
              </ConfirmAction>
              <ConfirmAction onConfirm={() => bulkSegna(false)} title={`Segnare ${selected.length} persone come ASSENTI?`} testId="presenze-bulk-assente-confirm">
                <button type="button" disabled={selected.length === 0} className="rounded-full bg-red-500 hover:bg-red-600 text-white text-xs font-bold px-3 py-2 disabled:opacity-40 inline-flex items-center gap-1.5" data-testid="presenze-bulk-assente-btn">
                  <X className="h-3.5 w-3.5" /> Segna assenti
                </button>
              </ConfirmAction>
            </>
          } />
      )}

      {!squadraId ? (
        <Empty icon={ClipboardCheck} text={canStaff ? "Nessuna squadra creata" : "Non sei assegnato a nessuna squadra"} />
      ) : !ctx ? (
        <p className="text-slate-400 font-semibold">Caricamento...</p>
      ) : (
        <>
          {!ctx.giorno_attivo && (
            <div className="rounded-3xl bg-red-50 border border-red-200 p-4 mb-5 flex items-center gap-3" data-testid="giorno-non-attivo-banner">
              <AlertTriangle className="h-5 w-5 text-red-500 shrink-0" />
              <p className="text-sm font-bold text-red-700">Giorno non attivo: le presenze non possono essere registrate (configura i giorni attivi in Impostazioni).</p>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            <div className="card-app p-5" data-testid="presenze-animati-card">
              <h2 className="font-display font-extrabold text-slate-900 mb-3">Animati ({ctx.animati.length})</h2>
              {ctx.animati.length === 0 ? (
                <p className="text-sm text-slate-400 font-semibold">Nessun Animato in squadra</p>
              ) : (
                ctx.animati.map((a) => (
                  <RigaPersona key={a.id} persona={a} stato={ctx.presenze[`animato:${a.id}`]}
                    onSet={(v) => ctx.giorno_attivo && segna(a.id, "animato", v)} testPrefix="animato"
                    bulkMode={bulkMode} isSelected={selected.includes(`animato:${a.id}`)}
                    onToggleSel={() => toggleSel(`animato:${a.id}`)} />
                ))
              )}
            </div>

            {canStaff && (
              <div className="card-app p-5" data-testid="presenze-staff-card">
                <h2 className="font-display font-extrabold text-slate-900 mb-3">Animatori / Aiuti ({ctx.staff.length})</h2>
                {ctx.staff.length === 0 ? (
                  <p className="text-sm text-slate-400 font-semibold">Nessun animatore in squadra</p>
                ) : (
                  ctx.staff.map((s) => (
                    <RigaPersona key={s.id} persona={s} stato={ctx.presenze[`staff:${s.id}`]}
                      onSet={(v) => ctx.giorno_attivo && segna(s.id, "staff", v)} testPrefix="staff"
                      bulkMode={bulkMode} isSelected={selected.includes(`staff:${s.id}`)}
                      onToggleSel={() => toggleSel(`staff:${s.id}`)} />
                  ))
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
